import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaSearch, FaCheckDouble, FaEllipsisV, FaTrash, FaEdit,
  FaTruck, FaMapMarkerAlt, FaPlus
} from 'react-icons/fa';
import { RiDeleteBin5Fill, RiBox3Fill } from "react-icons/ri";
import { IoMdArrowDropdown } from "react-icons/io";
import { IoSettingsSharp } from "react-icons/io5";
import { FaCircleUser } from "react-icons/fa6";
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosConfig';
import './Suppliers.css';

const Suppliers = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // --- States ---
  const [suppliersData, setSuppliersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);

  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [loadingCities, setLoadingCities] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentSupplierId, setCurrentSupplierId] = useState(null);

  const [formData, setFormData] = useState({
    title: '', firstName: '', middleName: '', lastName: '', suffix: '',
    companyName: '', supplierDisplayName: '', email: '', phoneNumber: '',
    mobileNumber: '', fax: '', other: '', website: '', streetAddress: '',
    city: '', country: '', postcode: '', state: '', balance: '0.00'
  });

  // Extract shopId from URL
  const [currentShopId, setCurrentShopId] = useState(null);
  useEffect(() => {
    const match = location.pathname.match(/\/shop\/(\d+)/);
    if (match) setCurrentShopId(parseInt(match[1]));
    else {
      const params = new URLSearchParams(location.search);
      const shopId = params.get('shop_id');
      setCurrentShopId(shopId ? parseInt(shopId) : null);
    }
  }, [location.pathname, location.search]);

  // --- API Calls ---
  const fetchSuppliers = useCallback(async (cityFilter = null) => {
    try {
      setLoading(true);
      let url = '/user/suppliers/';
      const params = new URLSearchParams();
      if (currentShopId) params.append('shop_id', currentShopId);
      if (cityFilter && cityFilter !== '') params.append('city', cityFilter);

      const queryString = params.toString();
      if (queryString) url += `?${queryString}`;

      const response = await axiosInstance.get(url);
      setSuppliersData(response.data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      // ❌ Popup hata diya taake 404 aane par baar baar error na aaye
      setSuppliersData([]);
    } finally {
      setLoading(false);
    }
  }, [currentShopId]);

  const fetchShopCities = async (shopId) => {
    if (!shopId) return;
    try {
      setLoadingCities(true);
      const response = await axiosInstance.get(`/user/shop-cities/?shop_id=${shopId}`);
      let citiesData = [];
      if (response.data) {
        if (Array.isArray(response.data.cities)) citiesData = response.data.cities;
        else if (Array.isArray(response.data)) citiesData = response.data;
      }
      setCities(citiesData);
    } catch (err) {
      console.error('Error fetching cities:', err);
    } finally {
      setLoadingCities(false);
    }
  };

  useEffect(() => {
    fetchSuppliers(selectedCity);
    if (currentShopId) fetchShopCities(currentShopId);
  }, [currentShopId, selectedCity, fetchSuppliers]);

  // --- Handlers ---
  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
    setSelectedSuppliers([]);
  };

  const filteredSuppliers = suppliersData.filter(supplier => {
    const s = supplier.supplier || supplier.companyName || '';
    const e = supplier.email || '';
    return s.toLowerCase().includes(searchTerm.toLowerCase()) ||
           e.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  // Stats
  const totalSuppliers = suppliersData.length;
  const totalOpenBalance = suppliersData.reduce((sum, s) => sum + (s.openBalance || 0), 0);
  const totalOverdue = 8087.27;
  const totalPaid = 3985.69;

  const toggleSupplierSelection = (supplier) => {
    setSelectedSuppliers(prev => {
      const isSelected = prev.some(s => s.id === supplier.id);
      return isSelected ? prev.filter(s => s.id !== supplier.id) : [...prev, supplier];
    });
  };

  const handleSelectAll = () => {
    if (selectedSuppliers.length === filteredSuppliers.length && filteredSuppliers.length > 0) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers([...filteredSuppliers]);
    }
  };

  const handleDeleteSupplier = async (supplier) => {
    const result = await Swal.fire({
      title: 'Delete Supplier?',
      html: `Are you sure you want to delete <strong>${supplier.supplier}</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/user/suppliers/${supplier.id}/`);
        fetchSuppliers(selectedCity);
        setSelectedSuppliers(prev => prev.filter(s => s.id !== supplier.id));
        Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
      } catch (err) {
        Swal.fire('Error', 'Failed to delete supplier', 'error');
      }
    }
  };

  const handleDeleteSelected = async () => {
    const result = await Swal.fire({
      title: 'Delete Selected?',
      text: `Delete ${selectedSuppliers.length} supplier(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, Delete',
    });

    if (result.isConfirmed) {
      try {
        for (const supplier of selectedSuppliers) {
          await axiosInstance.delete(`/user/suppliers/${supplier.id}/`);
        }
        fetchSuppliers(selectedCity);
        setSelectedSuppliers([]);
        setShowActionDropdown(false);
        Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
      } catch (err) {
        Swal.fire('Error', 'Failed to delete selected', 'error');
      }
    }
  };

  // Form Handlers
  const resetForm = () => setFormData({
    title: '', firstName: '', middleName: '', lastName: '', suffix: '',
    companyName: '', supplierDisplayName: '', email: '', phoneNumber: '',
    mobileNumber: '', fax: '', other: '', website: '', streetAddress: '',
    city: '', country: '', postcode: '', state: '', balance: '0.00'
  });

  const handleNewSupplier = () => {
    setIsEditMode(false);
    setCurrentSupplierId(null);
    resetForm();
    setIsModalOpen(true);
  };

  const handleEditSupplier = (supplier) => {
    setIsEditMode(true);
    setCurrentSupplierId(supplier.id);
    setFormData({
      ...supplier,
      supplierDisplayName: supplier.supplier,
      phoneNumber: supplier.phone,
      balance: supplier.openBalance?.toString() || '0.00'
    });
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      supplier: formData.supplierDisplayName,
      phone: formData.phoneNumber,
      openBalance: parseFloat(formData.balance) || 0
    };

    try {
      if (isEditMode) {
        await axiosInstance.put(`/user/suppliers/${currentSupplierId}/`, payload);
        Swal.fire({ icon: 'success', title: 'Updated!', timer: 1500, showConfirmButton: false });
      } else {
        await axiosInstance.post('/user/suppliers/', payload);
        Swal.fire({ icon: 'success', title: 'Added!', timer: 1500, showConfirmButton: false });
      }
      setIsModalOpen(false);
      fetchSuppliers(selectedCity);
    } catch (err) {
      Swal.fire('Error', 'Something went wrong while saving.', 'error');
    }
  };

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showActionDropdown]);

  return (
    <div className="suppliers-container">

      {/* 1. TOP ROW: Title Left, Controls Right */}
      <div className="suppliers-top-row">
        <div className="suppliers-title-area">
          <FaTruck className="suppliers-header-icon" />
          <h2 className="suppliers-header-title">Suppliers</h2>
        </div>

        <div className="suppliers-controls-area">
          {/* City Dropdown */}
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedCity}
              onChange={handleCityChange}
              disabled={loadingCities || cities.length === 0}
            >
              <option value="">{loadingCities ? 'Loading...' : 'All Cities'}</option>
              {cities.map((city, i) => <option key={i} value={city}>{city}</option>)}
            </select>
            <IoMdArrowDropdown className="city-dropdown-icon" />
          </div>

          {/* Search */}
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search suppliers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Select All */}
          <button className="select-all-btn" onClick={handleSelectAll}>
            <FaCheckDouble />
            <span>{selectedSuppliers.length === filteredSuppliers.length && filteredSuppliers.length > 0 ? 'Deselect All' : 'Select All'}</span>
          </button>

          {/* New Supplier */}
          <button className="add-user-btn" onClick={handleNewSupplier}>
            <FaPlus />
            <span>NEW SUPPLIER</span>
          </button>
        </div>
      </div>

      {/* 2. MIDDLE ROW: Stats + Icons only (tabs removed) */}
      <div className="suppliers-nav-row">
        <div className="nav-right-area">
          <div className="stats-container">
            <div className="stat-card total-suppliers-card">
              <div className="stat-label">Total Suppliers</div>
              <div className="stat-value">{totalSuppliers}</div>
            </div>
            <div className="stat-card overdue-card">
              <div className="stat-label">Overdue</div>
              <div className="stat-value">{formatCurrency(totalOverdue)}</div>
            </div>
            <div className="stat-card openbalance-card">
              <div className="stat-label">Open Balance</div>
              <div className="stat-value">{formatCurrency(totalOpenBalance)}</div>
            </div>
            <div className="stat-card totalpaid-card">
              <div className="stat-label">Total Paid</div>
              <div className="stat-value">{formatCurrency(totalPaid)}</div>
            </div>
          </div>

          <div className="nav-icons">
            <RiBox3Fill className="nav-icon" onClick={() => navigate('/')} />
            <IoSettingsSharp className="nav-icon" onClick={() => navigate('/settings')} />
            <FaCircleUser className="nav-icon" onClick={() => navigate('/profile')} />
          </div>
        </div>
      </div>

      {/* 3. TABLE SECTION */}
      <div className="suppliers-content">
        {selectedSuppliers.length > 0 && (
          <div className="selected-summary">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">{selectedSuppliers.length} supplier(s) selected</span>
              <div className="action-dropdown-container">
                <button className="action-dropdown-btn" onClick={() => setShowActionDropdown(!showActionDropdown)}>
                  <FaEllipsisV /> <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="action-dropdown-menu">
                    <button className="dropdown-item delete-item" onClick={handleDeleteSelected}>
                      <FaTrash /> <span>Delete Selected</span>
                    </button>
                  </div>
                )}
              </div>
              <button className="clear-selection" onClick={() => setSelectedSuppliers([])}>Clear all</button>
            </div>
          </div>
        )}

        <div className="suppliers-table-container">
          <div className="suppliers-table-wrapper">
            <table className="suppliers-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedSuppliers.length === filteredSuppliers.length && filteredSuppliers.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Supplier</th>
                  <th>Company Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>City</th>
                  <th>Open Balance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>Loading suppliers...</td></tr>
                ) : filteredSuppliers.length > 0 ? (
                  filteredSuppliers.map((supplier) => (
                    <tr key={supplier.id} className={selectedSuppliers.some(s => s.id === supplier.id) ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedSuppliers.some(s => s.id === supplier.id)}
                          onChange={() => toggleSupplierSelection(supplier)}
                        />
                      </td>
                      <td><div className="supplier-name-cell">{supplier.supplier}</div></td>
                      <td>{supplier.companyName}</td>
                      <td>{supplier.phone}</td>
                      <td>{supplier.email}</td>
                      <td><span className="supplier-city-cell">{supplier.city || 'N/A'}</span></td>
                      <td><span className="balance-amount">{formatCurrency(supplier.openBalance)}</span></td>
                      <td className="actions-cell">
                        <div className="actions-wrapper">
                          <button className="edit-address-btn" onClick={() => handleEditSupplier(supplier)}><FaEdit /></button>
                          <button className="delete-user-btn" onClick={() => handleDeleteSupplier(supplier)}><RiDeleteBin5Fill /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="no-users">
                      <FaTruck className="no-data-icon" />
                      <p>No suppliers found.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 5. MODAL */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{isEditMode ? 'Edit Supplier' : 'New Supplier'}</h2>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h3 className="section-title">Names and Contacts</h3>
                <div className="form-row">
                  <div className="form-group"><label>TITLE</label><input type="text" name="title" value={formData.title} onChange={handleInputChange} /></div>
                  <div className="form-group"><label>FIRST NAME</label><input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} /></div>
                  <div className="form-group"><label>MIDDLE NAME</label><input type="text" name="middleName" value={formData.middleName} onChange={handleInputChange} /></div>
                  <div className="form-group"><label>LAST NAME</label><input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} /></div>
                  <div className="form-group"><label>SUFFIX</label><input type="text" name="suffix" value={formData.suffix} onChange={handleInputChange} /></div>
                </div>

                <div className="form-row">
                  <div className="form-group"><label>COMPANY NAME</label><input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} /></div>
                  <div className="form-group"><label>SUPPLIER DISPLAY_NAME</label><input type="text" name="supplierDisplayName" value={formData.supplierDisplayName} onChange={handleInputChange} /></div>
                </div>

                <div className="form-row">
                  <div className="form-group"><label>EMAIL</label><input type="email" name="email" value={formData.email} onChange={handleInputChange} /></div>
                  <div className="form-group"><label>PHONE NUMBER</label><input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} /></div>
                </div>

                <div className="form-row">
                  <div className="form-group"><label>MOBILE NUMBER</label><input type="tel" name="mobileNumber" value={formData.mobileNumber} onChange={handleInputChange} /></div>
                  <div className="form-group"><label>FAX</label><input type="text" name="fax" value={formData.fax} onChange={handleInputChange} /></div>
                </div>

                <div className="form-row">
                  <div className="form-group"><label>OTHER</label><input type="text" name="other" value={formData.other} onChange={handleInputChange} /></div>
                  <div className="form-group"><label>WEBSITE</label><input type="url" name="website" value={formData.website} onChange={handleInputChange} /></div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="section-title">Address</h3>
                <div className="form-row">
                  <div className="form-group full-width"><label>Street Address</label><input type="text" name="streetAddress" value={formData.streetAddress} onChange={handleInputChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>CITY</label><input type="text" name="city" value={formData.city} onChange={handleInputChange} /></div>
                  <div className="form-group"><label>COUNTRY</label><input type="text" name="country" value={formData.country} onChange={handleInputChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>POSTCODE</label><input type="text" name="postcode" value={formData.postcode} onChange={handleInputChange} /></div>
                  <div className="form-group"><label>STATE</label><input type="text" name="state" value={formData.state} onChange={handleInputChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>BALANCE (£)</label><input type="number" name="balance" step="0.01" value={formData.balance} onChange={handleInputChange} /></div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-save">{isEditMode ? 'Update Supplier' : 'Save Supplier'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;